<template>
	<v-app class="console-expense" :theme="theme">
		<router-view
			name="appbar"
			:class="appBarClasses"
			@toggle-rail="toggleRail"
			:auth="auth"
			:no-toggle-rail="!auth"
			:rail="rail"
			@toggle-billing-report="openBillingDialog"
		/>
		<router-view
			name="nav"
			temporary
			:rail="rail"
			:entries="entries"
			:fixed-nav-entries="fixedNavEntries"
			:app-nav-title="appNavTitle"
		/>
		<app-notifications />
		<v-main class="ce-main">
			<router-view />
			<billing-activity-dialog :model-value="billingDialogVisible" @update:model-value="closeBillingDialog" />
		</v-main>
		<responsive-mobile-banner v-if="isMobile" />
	</v-app>
</template>

<style lang="scss" scoped>
.ce-main {
	position: relative;
	display: flex;
	flex-direction: column;
	min-height: calc(100vh - var(--ce-app-bar-height));
	height: calc(100vh - var(--ce-app-bar-height));
}
</style>

<script lang="ts" setup>
import { computed, provide, readonly } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { VApp, VMain } from 'vuetify/components';

import { useBoolean } from '@silae/composables/boolean';
import { Locale, useLocale } from '@silae/composables/i18n';
import { authorizeKonamiUsage } from '@silae/composables/konami-code';
import { useTheme } from '@silae/composables/ui';
import { AppNotifications, BillingActivityDialog, ResponsiveMobileBanner } from '~/components';
import { useFavicon, useNavigation, useOnlineHelp, useResponsive } from '~/composables';
import { NavigationEntriesOptions } from '~/domain';
import { HomeRoute, ManageTemplatesRoute, SettingsRoute } from '~/pages';
import { updateLuxonConfiguration, updateVeeValidateConfig } from '~/plugins';
import { APP_NAVIGATION_RAIL_WIDTH, APP_NAVIGATION_WIDTH, APP_NAVIGATION_WIDTH_KEY } from '~/utils';

import { initApp } from './app.init';

/** Composables and computed */
initApp();
authorizeKonamiUsage(true);

const { theme } = useTheme();
useOnlineHelp();

useFavicon();
const route = useRoute();
const { locale } = useLocale();
const { locale: _i18nLocale } = useI18n({ useScope: 'global', locale: locale.value });

const auth = computed(() => !!route?.meta?.auth);
const { isMobile } = useResponsive();

const onLocaleUpdate = (newLocale: Locale) => {
	_i18nLocale.value = newLocale.value;
	updateLuxonConfiguration(newLocale);
	updateVeeValidateConfig(newLocale);
	document.documentElement.lang = newLocale.value;
};

useLocale(onLocaleUpdate);

const navigationOptions = computed<NavigationEntriesOptions>(() => {
	if (route?.fullPath?.includes(SettingsRoute.path)) {
		return {
			filterBySettingsNavEntry: true
		};
	}

	return {
		filterByNavEntry: true
	};
});
const fixedNavEntries = computed(() => {
	return [HomeRoute, ManageTemplatesRoute]
		.filter(routeConfig => {
			return routeConfig?.meta?.isAuthorized?.()?.value ?? true;
		})
		.map(routeConfig => asNavigationEntry(routeConfig));
});
const { appNavTitle, asNavigationEntry, entries, rail, toggleRail } = useNavigation(navigationOptions);
const appBarClasses = computed(() => (rail.value ? 'app-bar--rail' : ''));
const appNavigationWidth = computed(() => (rail.value ? APP_NAVIGATION_RAIL_WIDTH : APP_NAVIGATION_WIDTH));

// Provide app navigation width globaly so that component may react to it (e.g: side panels)
provide(APP_NAVIGATION_WIDTH_KEY, readonly(appNavigationWidth));

const { value: billingDialogVisible, enable: openBillingDialog, disable: closeBillingDialog } = useBoolean();
</script>
